// App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Navbar from "./Navbar";
import BasicFooter from "./BasicFooter";
import { colors } from "./constants/colors";
// import { trackPageView } from "./utils/analytics"; // Removed: GA4 handles pageviews automatically
// Eagerly load components
import LocationComponent from "./LocationComponent";
import BookselfComponent from "./BookshelfComponent";
import BubbleComponent from "./BubbleComponent";
import SwipeCarouselComponent from "./SwipeCarouselComponent";
import ImageGallery from "./ImageGallery";
import ColoredBallsBackground from "./ColoredBallsBackground";
import ProfilePicture from "./ProfilePicture";
// Import ReactMediaPlayer (but not demo)
import ReactMediaPlayer from "./ReactMediaPlayer";
import CustomCursor from "./CustomCursor";
import ContactModal from "./ContactModal";
import Home from "./Home"; // Import the Home component
import About from "./About"; // Import the About component
import Gallery from "./Gallery"; // Import the Gallery component
import NotFound from "./components/NotFound"; // Import the NotFound component for 404 page
import PageTransition from "./components/PageTransition"; // Import our PageTransition component
import usePageTransition from "./hooks/usePageTransition"; // Import our custom transition hook
import { getRouteTransition, getTransitionClass } from "./utils/transitions"; // Import transition utilities
import ScrollToTop from "./components/ScrollToTop"; // Import our enhanced ScrollToTop component
import ScrollManager from "./components/ScrollManager"; // Import our new ScrollManager component
import initScrollTheme from "./utils/scrollTheme"; // Import our scrollbar theme utility
import SplashScreen from "./SplashScreen"; // Import our new SplashScreen component

// Main content wrapper
const MainContent = ({
  openContactModal,
  runHomeIntroAnimation,
  onHomeIntroAnimationComplete,
  isNavbarVisible,
}) => {
  // Use our custom transition hook to handle transitions properly
  const { transitionStage, handleAnimationEnd, displayLocation } =
    usePageTransition();

  // Determine which transition to use based on navigation direction
  const [transition, setTransition] = useState(null);

  // Update transition type when paths change - use direct path
  useEffect(() => {
    const transitionType = getRouteTransition(null, displayLocation.pathname);
    setTransition(transitionType);
  }, [displayLocation.pathname]);

  // Get proper transition class based on transition type and stage
  const transitionClass = transition
    ? getTransitionClass(transition, transitionStage)
    : "";

  // Handle the /contact path by opening the modal
  useEffect(() => {
    if (displayLocation.pathname === "/contact") {
      openContactModal();
    }
  }, [displayLocation.pathname, openContactModal]);

  // Determine if we're on the home page
  const isHomePage = displayLocation.pathname === "/";

  // Check if current path is one of the valid routes
  const validRoutes = ["/", "/about", "/photography", "/media", "/contact"];
  const isValidRoute = validRoutes.includes(displayLocation.pathname);

  // Determine page-specific class for background color based on direct path
  let pageClass = "";
  if (isHomePage) {
    pageClass = "home-page-bg";
  } else if (displayLocation.pathname === "/about") {
    pageClass = "about-page-bg";
  } else if (displayLocation.pathname === "/photography") {
    pageClass = "photography-page-bg";
  } else if (displayLocation.pathname === "/media") {
    pageClass = "media-page-bg";
  }

  // Create a transition container with proper animation handling
  const transitionProps = {
    className: `page-transition ${transitionStage} ${transitionClass} ${pageClass}`,
    onAnimationEnd: handleAnimationEnd,
  };

  return (
    <div className={`app-container`}>
      <div className="content-wrapper">
        <Navbar
          openContactModal={openContactModal}
          isVisible={isNavbarVisible && isValidRoute}
        />
        <main className="main-content">
          <div {...transitionProps}>
            {/* Use displayLocation instead of direct React Router location */}
            {displayLocation.pathname === "/" && (
              <Home
                runIntroAnimation={runHomeIntroAnimation}
                onIntroAnimationComplete={onHomeIntroAnimationComplete}
              />
            )}
            {displayLocation.pathname === "/about" && <About />}
            {displayLocation.pathname === "/photography" && <Gallery />}
            {displayLocation.pathname === "/media" && (
              <div className="media-page">
                <div className="media-content">
                  <div className="media-page-title">
                    <h1>Watch My Latest Work</h1>
                    <p>
                      Enjoy the immersive experience and discover my creative
                      vision
                    </p>
                  </div>

                  {/* "Beyond Erasure" - Above Player */}
                  <div className="video-prelude-caption">
                    <p className="caption-title">Beyond Erasure</p>
                  </div>

                  <ReactMediaPlayer url="/videos/be.mp4" />

                  {/* Project description */}
                  <div className="project-description">
                    <p>
                      This project entailed in-depth research into the Amarna
                      period, including Akhenaten's monotheistic revolution and
                      the era's distinctive art style, alongside technical
                      exploration of software like Unreal Engine and Adobe Media
                      Encoder. Key challenges included overcoming Unreal
                      Engine's learning curve and its limitations in
                      representing diverse ethnicities, which prompted an
                      unsuccessful search for Egyptian student models. The final
                      video adopted an immersive essay style, used AI narration
                      for accessibility, and featured historically inspired
                      music by Michael Atherton, aiming to engage an audience of
                      history enthusiasts and digital media consumers.
                    </p>
                  </div>

                  {/* "Stay tuned for more..." - Further down */}
                  <div className="media-page-epilogue">
                    <p className="caption-subtitle">Stay tuned for more...</p>
                  </div>
                </div>
              </div>
            )}
            {displayLocation.pathname === "/contact" && <Home />}
            {!isValidRoute && <NotFound />}
          </div>
        </main>
      </div>
      {isValidRoute && <BasicFooter openContactModal={openContactModal} />}
    </div>
  );
};

// App component with Router
const AppWithRouter = () => {
  return (
    <Router>
      <ScrollManager />
      <AppContent />
    </Router>
  );
};

// Actual App content
const AppContent = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [runHomeIntroAnimation, setRunHomeIntroAnimation] = useState(null);
  const [isNavbarVisible, setIsNavbarVisible] = useState(false);
  const [isInitialHomeIntro, setIsInitialHomeIntro] = useState(true);
  const root = document.documentElement;
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState(null);

  // Use direct location from React Router instead of usePageTransition
  const currentPath = location.pathname;

  // Track page views - Removed: GA4 handles this automatically
  // useEffect(() => {
  //   trackPageView(currentPath);
  // }, [currentPath]);

  // Determine if we should show the home background
  const isCurrentlyHome = currentPath === "/";

  // Track previous path to detect navigations to home page
  useEffect(() => {
    setPreviousPath(currentPath);
  }, [currentPath]);

  // Effect to run home intro animation on navigation to home page
  useEffect(() => {
    if (
      currentPath === "/" &&
      previousPath !== "/" &&
      !showSplash &&
      runHomeIntroAnimation !== true
    ) {
      setRunHomeIntroAnimation(true);
    }
  }, [currentPath, previousPath, showSplash, runHomeIntroAnimation]);

  // Initialize the scrollbar theme functionality
  useEffect(() => {
    // Initialize scrollbar theme
    const cleanupScrollTheme = initScrollTheme();

    // Return cleanup function
    return () => {
      cleanupScrollTheme();
    };
  }, []);

  // Apply master background color to all non-home pages
  useEffect(() => {
    // Apply CSS variables
    for (const palette in colors) {
      for (const colorName in colors[palette]) {
        const cssVariableName = `--${palette}-${colorName}`;
        root.style.setProperty(cssVariableName, colors[palette][colorName]);
      }
    }

    // Apply the home-bg-active class to body when on home page
    if (isCurrentlyHome) {
      document.body.classList.add("home-bg-active");
    } else {
      document.body.classList.remove("home-bg-active");
    }
  }, [isCurrentlyHome]);

  // Control Navbar visibility based on splash screen and home intro animation
  useEffect(() => {
    if (showSplash) {
      setIsNavbarVisible(false);
    } else if (runHomeIntroAnimation === true) {
      if (isInitialHomeIntro) {
        setIsNavbarVisible(false);
      } else {
        setIsNavbarVisible(true);
      }
    } else if (
      runHomeIntroAnimation === null &&
      isInitialHomeIntro &&
      currentPath === "/"
    ) {
      setIsNavbarVisible(false);
    } else {
      setIsNavbarVisible(true);
    }
  }, [showSplash, runHomeIntroAnimation, isInitialHomeIntro, currentPath]);

  // Initialize showSplash state based on current path
  useEffect(() => {
    // Only show splash screen on the home page
    if (!isCurrentlyHome) {
      setShowSplash(false);
      setIsNavbarVisible(true);
    }
  }, [isCurrentlyHome]);

  // Function to open contact modal
  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  // Function to close contact modal
  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  // Handler for when splash screen animation completes
  const handleSplashComplete = () => {
    setShowSplash(false);
    if (location.pathname === "/") {
      setRunHomeIntroAnimation(true);
    } else {
      setRunHomeIntroAnimation(false);
      setIsNavbarVisible(true);
      setIsInitialHomeIntro(false);
    }
  };

  const handleHomeIntroAnimationComplete = () => {
    setRunHomeIntroAnimation(false);
    setIsNavbarVisible(true);
    setIsInitialHomeIntro(false);
  };

  return (
    <>
      {/* Show splash screen initially only on home page */}
      {showSplash && isCurrentlyHome && (
        <SplashScreen onComplete={handleSplashComplete} />
      )}

      {/* Always render ColoredBallsBackground, but only make it visible on home page */}
      <ColoredBallsBackground isVisible={isCurrentlyHome} />

      {/* Add the custom cursor component here */}
      <CustomCursor />

      {/* Add the ScrollToTop component here */}
      <ScrollToTop />

      {/* Contact Modal will show when isContactModalOpen is true */}
      <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />

      <Routes>
        <Route
          path="*"
          element={
            <MainContent
              openContactModal={openContactModal}
              runHomeIntroAnimation={runHomeIntroAnimation}
              onHomeIntroAnimationComplete={handleHomeIntroAnimationComplete}
              isNavbarVisible={isNavbarVisible}
            />
          }
        />
      </Routes>
    </>
  );
};

export default AppWithRouter;
